/**
 * LandXML domain model.
 *
 * Coordinate convention
 * ---------------------
 * LandXML numeric point text is `northing easting [elevation]` (see the `Point`
 * simple type in the schema). Every point in this model has already been
 * converted to a right-handed **survey plane**: `x = easting`, `y = northing`,
 * `z = elevation`. The `northing easting` ordering therefore only ever appears
 * inside the parser.
 */

export type AngUnit = 'radians' | 'grads' | 'decimal degrees' | 'decimal dd.mm.ss';

export interface Units {
  system: 'metric' | 'imperial';
  linear: string;
  area?: string;
  volume?: string;
  /** Unit for `angle`-typed attributes (delta, theta, rotationAngle, angleSkew). */
  angular: AngUnit;
  /** Unit for `direction`-typed attributes (dir, dirStart, dirEnd). */
  direction: AngUnit;
  latLong?: AngUnit;
  /** Multiply a linear value by this to get metres. */
  linearToMeters: number;
  raw: Record<string, string>;
}

export interface Crs {
  name?: string;
  epsgCode?: string;
  ogcWktCode?: string;
  rotationAngle?: number;
  horizontalDatum?: string;
  verticalDatum?: string;
  ellipsoidName?: string;
  projectedCoordinateSystemName?: string;
  geographicCoordinateSystemName?: string;
  horizontalCoordinateSystemName?: string;
  verticalCoordinateSystemName?: string;
  /** All attributes verbatim, including vendor extensions. */
  raw: Record<string, string>;
}

/** A point that may carry coordinates, a `pntRef`, or both. */
export interface Pt {
  x?: number;
  y?: number;
  z?: number;
  pntRef?: string;
}

/** A point guaranteed to have x/y after reference resolution. */
export interface Pt2 {
  x: number;
  y: number;
  z?: number;
}

export interface CgPoint extends Pt2 {
  name: string;
  code?: string;
  desc?: string;
  state?: string;
  timeStamp?: string;
  oID?: string;
  role?: string;
  /** Non-schema vendor attributes (Leica latitude/longitude/ellipsoidHeight etc). */
  extra?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// CoordGeom
// ---------------------------------------------------------------------------

export type Rot = 'cw' | 'ccw';

export interface CgBase {
  /** Element name as it appeared, for the inspector. */
  kind: string;
  name?: string;
  desc?: string;
  staStart?: number;
  length?: number;
}

export interface LineEl extends CgBase {
  kind: 'Line';
  start: Pt;
  end: Pt;
  dir?: number; // radians, as-declared (checksum only)
}

export interface CurveEl extends CgBase {
  kind: 'Curve';
  rot: Rot;
  start: Pt;
  center?: Pt;
  end: Pt;
  pi?: Pt;
  radius?: number;
  delta?: number; // radians
  chord?: number;
  crvType?: 'arc' | 'chord';
  dirStart?: number;
  dirEnd?: number;
  tangent?: number;
  external?: number;
  midOrd?: number;
}

export interface SpiralEl extends CgBase {
  kind: 'Spiral';
  rot: Rot;
  spiType: string;
  length: number;
  radiusStart: number; // Infinity allowed
  radiusEnd: number;
  start: Pt;
  pi?: Pt;
  end: Pt;
  theta?: number;
  totalX?: number;
  totalY?: number;
  tanLong?: number;
  tanShort?: number;
  constant?: number;
  dirStart?: number;
  dirEnd?: number;
}

export interface IrregularLineEl extends CgBase {
  kind: 'IrregularLine';
  start?: Pt;
  end?: Pt;
  pts: Pt2[];
}

export interface ChainEl extends CgBase {
  kind: 'Chain';
  /** CgPoint names. */
  refs: string[];
}

export type CgElement = LineEl | CurveEl | SpiralEl | IrregularLineEl | ChainEl;

export interface CoordGeom {
  name?: string;
  desc?: string;
  elements: CgElement[];
}

// ---------------------------------------------------------------------------
// Alignments
// ---------------------------------------------------------------------------

export interface StaEquation {
  staInternal: number;
  staAhead: number;
  staBack?: number;
  increasing: boolean;
}

export type PviKind = 'PVI' | 'ParaCurve' | 'UnsymParaCurve' | 'CircCurve';

export interface PviEntry {
  kind: PviKind;
  sta: number;
  elev: number;
  length?: number;
  lengthIn?: number;
  lengthOut?: number;
  radius?: number;
  desc?: string;
}

export interface ProfAlign {
  name: string;
  entries: PviEntry[];
}

export interface ProfSurf {
  name: string;
  /** Runs of [station, elevation] pairs; each run is a gap-free segment. */
  runs: Array<Array<[number, number]>>;
}

export interface ProfileDef {
  name?: string;
  staStart?: number;
  surfaces: ProfSurf[];
  aligns: ProfAlign[];
}

export interface CrossSectSurf {
  name: string;
  /** [offset, elevation] pairs; negative offset = left of centreline. */
  pts: Array<[number, number]>;
  design?: boolean;
  side?: string;
  material?: string;
  /**
   * Vendor `dataFormat` from `<CrossSectPnt>`, e.g. "Slope Distance". When this is
   * set to anything other than an offset/elevation form the pairs are NOT plan
   * offsets and must not be plotted as such.
   */
  dataFormat?: string;
}

export interface CrossSect {
  sta: number;
  name?: string;
  desc?: string;
  areaCut?: number;
  areaFill?: number;
  surfs: CrossSectSurf[];
}

export interface CantStation {
  station: number;
  appliedCant?: number;
  speed?: number;
  curvature?: Rot;
}

/**
 * The PI-based horizontal geometry model: a list of intersection points, each
 * optionally carrying an entry spiral, one or two circular curves and an exit
 * spiral. AASHTO SDMS and several survey packages write this instead of
 * `<CoordGeom>`; the actual coordinates have to be computed from the PIs.
 */
export interface AlignPI {
  pi: Pt;
  station?: number;
  code?: string;
  name?: string;
  inSpiral?: SpiralEl;
  curve1?: CurveEl;
  midSpiral?: SpiralEl;
  curve2?: CurveEl;
  outSpiral?: SpiralEl;
}

export interface Alignment {
  name: string;
  desc?: string;
  length: number;
  staStart: number;
  state?: string;
  coordGeom: CoordGeom;
  /** Present instead of (or alongside) `coordGeom` in PI-based files. */
  alignPIs: AlignPI[];
  staEquations: StaEquation[];
  profiles: ProfileDef[];
  crossSects: CrossSect[];
  cant?: { name?: string; gauge?: number; stations: CantStation[] };
  features: FeatureNode[];
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export interface SurfPolyline {
  name?: string;
  type?: string;
  pts: Pt2[];
  /** true when the source list had no elevations (needs draping). */
  flat: boolean;
}

export interface Surface {
  name: string;
  desc?: string;
  surfType: 'TIN' | 'grid';
  /** Interleaved x,y,z per vertex (already de-referenced from P/@id). */
  xyz: Float64Array;
  /** Triangle vertex indices into `xyz`, 3 per face. Quads are split. */
  tris: Uint32Array;
  /** 1 = drawn, 0 = present in the triangulation but invisible (`F/@i`). */
  triVisible: Uint8Array;
  elevMin: number;
  elevMax: number;
  area2DSurf?: number;
  area3DSurf?: number;
  breaklines: SurfPolyline[];
  boundaries: SurfPolyline[];
  contours: SurfPolyline[];
}

// ---------------------------------------------------------------------------
// Parcels, plan features, pipes, monuments
// ---------------------------------------------------------------------------

export interface Parcel {
  name: string;
  desc?: string;
  parcelType?: string;
  state?: string;
  area?: number;
  owner?: string;
  center?: Pt;
  coordGeom?: CoordGeom;
  children: Parcel[];
}

export interface PlanFeature {
  name?: string;
  desc?: string;
  group?: string;
  coordGeoms: CoordGeom[];
  locations: Pt2[];
}

export interface PipeStruct {
  name: string;
  desc?: string;
  center?: Pt;
  elevRim?: number;
  elevSump?: number;
  shape?: string;
  diameter?: number;
  width?: number;
  length?: number;
}

export interface PipeRun {
  name: string;
  refStart?: string;
  refEnd?: string;
  length?: number;
  slope?: number;
  shape?: string;
  diameter?: number;
  material?: string;
}

export interface PipeNetwork {
  name: string;
  pipeNetType?: string;
  structs: PipeStruct[];
  pipes: PipeRun[];
}

export interface Monument {
  name: string;
  pntRef?: string;
  desc?: string;
  type?: string;
  state?: string;
  condition?: string;
}

// ---------------------------------------------------------------------------
// Extension mechanism + diagnostics
// ---------------------------------------------------------------------------

export interface FeatureNode {
  code?: string;
  source?: string;
  name?: string;
  props: Record<string, string>;
  children: FeatureNode[];
}

export type WarnLevel = 'info' | 'warn' | 'error';

export interface Warning {
  level: WarnLevel;
  code: string;
  message: string;
  /** How many times the same code fired; the parser folds duplicates. */
  count: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export function emptyBounds(): Bounds {
  return {
    minX: Infinity, minY: Infinity, minZ: Infinity,
    maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity,
  };
}

export function growBounds(b: Bounds, x: number, y: number, z?: number): void {
  if (x < b.minX) b.minX = x;
  if (x > b.maxX) b.maxX = x;
  if (y < b.minY) b.minY = y;
  if (y > b.maxY) b.maxY = y;
  if (z !== undefined && Number.isFinite(z)) {
    if (z < b.minZ) b.minZ = z;
    if (z > b.maxZ) b.maxZ = z;
  }
}

export function boundsValid(b: Bounds): boolean {
  return Number.isFinite(b.minX) && Number.isFinite(b.maxX);
}

export interface LandXmlDoc {
  version: string;
  namespace?: string;
  date?: string;
  time?: string;
  language?: string;
  project?: { name?: string; desc?: string };
  application?: { name?: string; version?: string; manufacturer?: string };
  units: Units;
  crs?: Crs;
  cgPoints: CgPoint[];
  cgPointsByName: Map<string, CgPoint>;
  surfaces: Surface[];
  alignments: Alignment[];
  parcels: Parcel[];
  planFeatures: PlanFeature[];
  pipeNetworks: PipeNetwork[];
  monuments: Monument[];
  features: FeatureNode[];
  warnings: Warning[];
  bounds: Bounds;
  /** Bytes of source XML, for the status bar. */
  sourceBytes: number;
  fileName?: string;
}
